import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { payrollRunKeys, payslipKeys, employeeKeys, accountKeys, transactionKeys, expenseKeys } from '@/lib/query-keys';
import { payrollRunService, payslipEmailService } from '@/services/payroll.service';
import { usePayrollRunFilterStore } from '@/stores/payrollRunFilterStore';

function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function usePayrollRuns() {
  const queryClient = useQueryClient();
  const filters = usePayrollRunFilterStore();

  // The store keeps month/year as strings for the Select components; the API
  // wants numbers, and empty strings must not be sent at all.
  const toApiParams = (f: typeof filters.current) => {
    const params: Record<string, unknown> = { ...f };
    if (params.periodMonth) params.periodMonth = Number(params.periodMonth);
    else delete params.periodMonth;
    if (params.periodYear) params.periodYear = Number(params.periodYear);
    else delete params.periodYear;
    return params;
  };

  const runsQuery = useQuery({
    queryKey: payrollRunKeys.list(filters.current),
    queryFn: () => payrollRunService.getAll(toApiParams(filters.current)),
    placeholderData: keepPreviousData,
  });

  const invalidate = (id?: string) => {
    queryClient.invalidateQueries({ queryKey: payrollRunKeys.all });
    if (id) queryClient.invalidateQueries({ queryKey: payrollRunKeys.detail(id) });
    queryClient.invalidateQueries({ queryKey: payslipKeys.all });
    queryClient.invalidateQueries({ queryKey: employeeKeys.all });
  };

  /** Approving moves money, so the ledger caches have to drop too. */
  const invalidateLedger = () => {
    queryClient.invalidateQueries({ queryKey: accountKeys.all });
    queryClient.invalidateQueries({ queryKey: transactionKeys.all });
    queryClient.invalidateQueries({ queryKey: expenseKeys.all });
  };

  const create = useMutation({
    mutationFn: (data: Record<string, unknown>) => payrollRunService.create(data),
    onSuccess: () => { invalidate(); toast.success('Payroll run created'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to create the payroll run')),
  });

  const submit = useMutation({
    mutationFn: (id: string) => payrollRunService.submit(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Sent for review'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to submit the run')),
  });

  const reopen = useMutation({
    mutationFn: (id: string) => payrollRunService.reopen(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Reopened as a draft'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to reopen the run')),
  });

  const approve = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { accountId: string; categoryId?: string } }) =>
      payrollRunService.approve(id, data),
    onSuccess: (_, { id }) => {
      invalidate(id); invalidateLedger();
      toast.success('Approved — payslips published and the cost posted to the ledger');
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to approve the run')),
  });

  const markPaid = useMutation({
    mutationFn: (id: string) => payrollRunService.markPaid(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Marked as paid'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to mark the run paid')),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => payrollRunService.cancel(id),
    onSuccess: (_, id) => { invalidate(id); toast.success('Payroll run cancelled'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to cancel the run')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => payrollRunService.delete(id),
    onSuccess: () => { invalidate(); toast.success('Payroll run deleted'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to delete the run')),
  });

  const emailAll = useMutation({
    mutationFn: (id: string) => payslipEmailService.emailRun(id),
    onSuccess: (res, id) => {
      invalidate(id);
      const { queued, skipped } = res.data;
      toast.success(
        skipped.length > 0
          ? `${queued.length} payslip(s) queued · ${skipped.length} skipped`
          : `${queued.length} payslip(s) queued for sending`,
      );
      // Surface each skip so a missing address is never silently swallowed.
      skipped.forEach((s) => toast.warning(`${s.payslipNumber}: ${s.reason}`));
    },
    onError: (e) => toast.error(apiMessage(e, 'Failed to queue the payslip emails')),
  });

  return {
    emailAll,
    runs: runsQuery.data?.data ?? [],
    meta: runsQuery.data?.meta,
    isLoading: runsQuery.isLoading,
    filters,
    create, submit, reopen, approve, markPaid, cancel, remove,
    exportCsv: () => payrollRunService.exportCsv(toApiParams(filters.current)),
  };
}

export function usePayrollRun(id: string) {
  const query = useQuery({
    queryKey: payrollRunKeys.detail(id),
    queryFn: () => payrollRunService.getById(id),
    enabled: !!id,
  });
  return { run: query.data?.data, isLoading: query.isLoading, refetch: query.refetch };
}

export function useRunEligibility(periodYear: number, periodMonth: number, enabled = true) {
  const query = useQuery({
    queryKey: payrollRunKeys.eligible(periodYear, periodMonth),
    queryFn: () => payrollRunService.eligible(periodYear, periodMonth),
    enabled: enabled && !!periodYear && !!periodMonth,
  });
  return { eligibility: query.data?.data, isLoading: query.isLoading };
}

/** Per-payslip editing inside a draft run. Each change refreshes the run totals. */
export function useRunPayslipEditor(runId: string) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: payrollRunKeys.detail(runId) });
    queryClient.invalidateQueries({ queryKey: payslipKeys.all });
  };

  const updatePayslip = useMutation({
    mutationFn: ({ payslipId, data }: { payslipId: string; data: { paidDays?: number; totalDays?: number; notes?: string } }) =>
      payrollRunService.updatePayslip(runId, payslipId, data),
    onSuccess: () => { invalidate(); toast.success('Payslip updated'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to update the payslip')),
  });

  const addAdjustment = useMutation({
    mutationFn: ({ payslipId, data }: { payslipId: string; data: { label: string; type: string; amount: number; notes?: string } }) =>
      payrollRunService.addAdjustment(runId, payslipId, data),
    onSuccess: () => { invalidate(); toast.success('Adjustment added'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to add the adjustment')),
  });

  const removeAdjustment = useMutation({
    mutationFn: ({ payslipId, adjustmentId }: { payslipId: string; adjustmentId: string }) =>
      payrollRunService.removeAdjustment(runId, payslipId, adjustmentId),
    onSuccess: () => { invalidate(); toast.success('Adjustment removed'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to remove the adjustment')),
  });

  const excludePayslip = useMutation({
    mutationFn: (payslipId: string) => payrollRunService.excludePayslip(runId, payslipId),
    onSuccess: () => { invalidate(); toast.success('Employee removed from the run'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to remove the employee')),
  });

  return { updatePayslip, addAdjustment, removeAdjustment, excludePayslip };
}
